import _ from "lodash";
import {action, computed, decorate, observable, toJS} from "mobx";
import moment from "moment";
import {ADD_WATERMARK, CROP_RATIOS, CROSS_FADE, DEFAULT_MLB_WATERMARK} from "../httpClients/ClippyApi";
import {toast} from "react-toastify";

const IGNORE_CATEGORIES = new Set(["PlayMetaData", "General", "Game"]);

class ReelStore {
  constructor(
    clippyApi,
    videoSearchStore,
    sportyApi,
    myClipsStore,
    basicSearchStore,
    authStore
  ) {
    this.clippyApi = clippyApi;
    this.videoSearchStore = videoSearchStore;
    this.sportyApi = sportyApi;
    this.myClipsStore = myClipsStore;
    this.basicSearchStore = basicSearchStore;
    this.authStore = authStore;
  }

  errorMessage = "";
  reelClips = [];
  clipMetadata = observable.map();
  isDialogOpen = false;
  isCreatingClip = false;
  fetchingClips = false;
  clipTitle = "";
  addWatermark = false;
  watermark = "None";
  applyCrossfade = false;
  titleError = false;
  cropRatio = "None";
  cropRatios = ["None", "1:1", "4:5", "9:16"];

  clear() {
    this.clipMetadata.clear();
    this.reelClips = [];
    this.clipTitle = "";
    this.titleError = false;
    this.addWatermark = false;
    this.applyCrossfade = false;
    this.cropRatio = "None";
    this.watermark = "None";
  }

  setCropRatio(cropRatio) {
    this.cropRatio = cropRatio;
  }

  setWatermark(watermark) {
    this.watermark = watermark;
    // If MLB watermark applied, supply legacy option
    // to enable legacy usage (recuts, etc.)
    this.addWatermark = watermark && watermark === DEFAULT_MLB_WATERMARK;
  }

  setClipTitle(title) {
    this.clipTitle = title;
    this.titleError = this.clipTitle === "";
  }

  setDialogOpen(open) {
    this.isDialogOpen = open;
  }

  getPlayForReel(playId) {
    const play = _.find(
      this.videoSearchStore.plays,
      p => p.get("id") === playId
    );
    return {
      id: playId,
      playId: playId,
      gamePk: play.get("gamePk"),
      feedType: play.get("feedType"),
      url: play.get("videoSrc"),
      thumbnailUri: this._getThumbnailFromPlayClip(play.get("videos")),
      startOffset: 0,
      endOffset: 0,
      commonPlaySearchMetadata: this.getCommonPlaySearchMetadata(),
      videos: play.get("videos"),
      type: "PLAY"
    };
  }

  addVideo(clip) {
    this.reelClips.push({
      id: this.getIdFromDirectClipUrl(clip.url),
      type: "DIRECT",
      url: clip.url,
      thumbnailUri: clip.thumbnailUri,
      startOffset: 0,
      endOffset: 0
    });
  }

  addClipFromClipInfo(clipInfo) {
    if (clipInfo.playId) {
      this.reelClips.push({
        id: clipInfo.playId,
        playId: clipInfo.playId,
        gamePk: clipInfo.playDetails.gamePk,
        feedType: clipInfo.feedType,
        url: clipInfo.playDetails.videos[0].link,
        thumbnailUri: this._getThumbnailFromPlayClip(
          clipInfo.playDetails.videos
        ),
        startOffset: clipInfo.startOffset,
        endOffset: clipInfo.endOffset,
        commonPlaySearchMetadata: [],
        videos: clipInfo.playDetails.videos,
        type: "PLAY"
      });
    } else {
      this.reelClips.push({
        id: this.getIdFromDirectClipUrl(clipInfo.url),
        startOffset: clipInfo.startOffset,
        endOffset: clipInfo.endOffset,
        url: clipInfo.url,
        thumbnailUri: clipInfo.thumbnailUri,
        type: "DIRECT"
      });
    }
  }

  addPlay(playId) {
    this.errorMessage = "";
    const play = this.getPlayForReel(playId);
    const existingPlayVideoSrcs = this.reelClips.map(rc => rc.url);
    if (!existingPlayVideoSrcs.includes(play.url)) {
      return this.reelClips.push(play);
    }
  }

  updateReelClip(index, attrs) {
    const clip = this.reelClips[index];
    Object.keys(attrs).forEach(function (key) {
      clip[key] = attrs[key];
    });

    // Set relativeEndOffset for clippy
    clip["relativeEndOffset"] = attrs["videoDuration"] - attrs["endOffset"];

    if ("startOffset" in attrs) {
      this.clipMetadata.get(clip["url"])["durationDelta"] =
        attrs["videoDuration"] - attrs["endOffset"] + attrs["startOffset"];
    }
  }

  addClipMetadata(video) {
    this.clipMetadata.set(video.src, {
      duration: video.duration,
      durationDelta: 0
    });
  }

  concatPlays(playIds) {
    playIds.foreach(playId => this.addPlay(playId));
  }

  restoreReel(clipId) {
    this.errorMessage = "";
    this.fetchingClips = true;
    this.isDialogOpen = true;
    this.sportyApi
      .getClipInfo(clipId)
      .then(ci => {
        _.sortBy(
          _.concat(ci["plays"], ci["videos"]),
          v => v.clipPosition
        ).forEach(v => this.addClipFromClipInfo(v));

        // set controls
        this.setControlsFromOptions(ci.options);
      })
      .catch(err => {
        console.log(err);
        toast.error("Failed to load clips", {
          position: "top-right",
          autoClose: 3000
        });
        this.isDialogOpen = false;
      })
      .finally(() => {
        this.fetchingClips = false;
      });
  }

  setControlsFromOptions(options) {
    if (!options) {
      return;
    }

    if (options.includes(ADD_WATERMARK)) {
      this.addWatermark = true;
      this.watermark = DEFAULT_MLB_WATERMARK;
    }

    if (options.includes(CROSS_FADE)) {
      this.applyCrossfade = true;
    }

    for (const cr in CROP_RATIOS) {
      const optionValue = CROP_RATIOS[cr];
      if (options.includes(optionValue)) {
        this.cropRatio = cr;
      }
    }
  }

  createClip() {
    if (!this.validateReel()) {
      return;
    }

    const clips = this.reelClips;
    this.isCreatingClip = true;
    this.isDialogOpen = false;
    return this.clippyApi
      .createUserClip(
        clips,
        this.applyCrossfade,
        true,
        this.cropRatio,
        this.watermark
      )
      .then(async resp => {
        const clipResp = resp
          ? _.find(resp, clip => clip.clipOutputType === "MP4")
          : {};
        const {
          durationInSeconds,
          id,
          uri,
          thumbnails,
          width,
          height
        } = clipResp;
        const time = this._getCurrentTime();
        const name =
          this.clipTitle === "" ? ` Untitled: ${time}` : this.clipTitle;
        const thumbnailUri =
          thumbnails && thumbnails.length > 0 ? thumbnails[0].uri : "";
        const commonClipSearchMetadata = this.getCommonClipSearchMetadata(
          clips
        );

        await this.sportyApi.putUserClip({
          title: name,
          id: id,
          clips: clips,
          addWatermark: this.addWatermark,
          applyCrossfade: this.applyCrossfade,
          cropRatio: this.cropRatio,
          videoUri: uri,
          thumbnailUri,
          width,
          height,
          duration: durationInSeconds,
          searchCategory: commonClipSearchMetadata.category,
          username: this.authStore.user.preferred_username
        });

        this.myClipsStore.refreshClips();
        this.clear();

        toast.success("Successfully created new clip", {
          position: "top-right",
          autoClose: 3000
        });
      })
      .catch(err => {
        console.log(err);
        toast.error("Failed to create clip. Please try again.", {
          position: "top-right",
          autoClose: 3000
        });
      })
      .finally(() => {
        this.isCreatingClip = false;
      });
  }

  createQuickClip(plays) {
    const clippyPlays = plays.map(play => ({
      playId: play.get("id"),
      gamePk: play.get("gamePk"),
      feedType: play.get("feedType"),
      type: "PLAY"
    }));

    return this.clippyApi
      .createUserClip(clippyPlays)
      .then(async resp => {
        const clipResp = resp ? resp[0] : {};
        const {
          durationInSeconds,
          id,
          uri,
          thumbnails,
          width,
          height
        } = clipResp;
        const thumbnailUri = thumbnails.length > 0 ? thumbnails[0].uri : "";
        const commonClipSearchMetadata = this.getCommonPlaySearchMetadata();
        const title = "QuickClip: " + this._getCurrentTime();

        await this.sportyApi.putUserClip({
          title: title,
          id: id,
          clips: clippyPlays,
          addWatermark: this.addWatermark,
          applyCrossfade: this.applyCrossfade,
          cropRatio: this.cropRatio,
          videoUri: uri,
          thumbnailUri,
          width,
          height,
          duration: durationInSeconds,
          searchCategory: commonClipSearchMetadata.category,
          username: this.authStore.user.preferred_username
        });

        this.myClipsStore.refreshClips();

        toast.success("Successfully created new clip", {
          position: "top-right",
          autoClose: 3000
        });
      })
      .catch(err => {
        console.log(err);
        toast.error("Failed to create quick clip. Please try again.", {
          position: "top-right",
          autoClose: 3000
        });
      });
  }

  removeClip(id) {
    const [clipsToKeep, clipsToRemove] = _.partition(
      this.reelClips.slice(),
      clip => clip.id !== id
    );

    clipsToRemove.forEach(clip => {
      this.clipMetadata.delete(clip.url);
    });

    this.reelClips = clipsToKeep;
  }

  _getCurrentTime() {
    // Example time string generated: 2:32 PM (09/20/18)
    return moment().format("h:mm A (MM/DD/YY)");
  }

  moveClip(startIndex, endIndex) {
    const newReelClips = toJS(this.reelClips);
    newReelClips.splice(endIndex, 0, newReelClips.splice(startIndex, 1)[0]);

    this.reelClips = newReelClips;
  }

  get reelDuration() {
    return _.chain(toJS(this.clipMetadata))
      .values()
      .map(o => o.duration - o.durationDelta)
      .reduce((current, accumulation) => current + accumulation)
      .value();
  }

  get reelDurationStr() {
    const durationInSeconds = this.reelDuration;

    if (!durationInSeconds) {
      return "--:--";
    }

    const date = new Date(null);
    date.setSeconds(durationInSeconds);
    return date.toISOString().substr(14, 5);
  }

  /**
   * Check to see if all search clauses have same category
   * We only care about Batter, Fielder, Runner, and Pitcher
   */
  getCommonPlaySearchMetadata() {
    return this.basicSearchStore.clauses
      .filter(clause => !IGNORE_CATEGORIES.has(clause.category))
      .map(clause => {
        if (clause.values.length) {
          const label = clause.values[0].label.toLowerCase();
          //strikeouts and labels are part of hit result,
          // but we want them to show as pitching for tags.
          if (label === "strikeout" || label === "walk") {
            return {
              ...clause,
              category: "Pitcher"
            };
          }
        }
        return clause;
      })
      .reduce((current, next) => this.compareFields(current, next), {
        category: "INITIAL"
      });
  }

  /**
   * @param plays - list of plays in the clip
   * Check if all plays in a clip have the same SearchMetadata
   */
  getCommonClipSearchMetadata(plays) {
    return plays
      .filter(play => play.commonPlaySearchMetadata)
      .map(play => play.commonPlaySearchMetadata)
      .reduce((current, next) => this.compareFields(current, next), {
        category: "INITIAL"
      });
  }

  compareFields(current, next) {
    let nextFieldName = next.fieldName;
    let nextCategory = next.category;
    if (current.category === "INITIAL") {
      return next;
    }
    if (current.category !== nextCategory) {
      nextCategory = "";
    }
    if (current.fieldName !== nextFieldName) {
      nextFieldName = "";
    }
    return {category: nextCategory, fieldName: nextFieldName};
  }

  getIdFromDirectClipUrl(url) {
    const parts = url.split("/");
    return parts[parts.length - 1].split(".")[0];
  }

  validateReel() {
    this.titleError = this.clipTitle === "";

    return !this.titleError;
  }

  _getThumbnailFromPlayClip(feeds) {
    if (feeds && feeds.length) {
      const currentFeed = feeds[0];
      const thumbnail = currentFeed.image;
      if (thumbnail && thumbnail.cuts.length) {
        return thumbnail.cuts[0].at2x;
      }
    }
  }
}

decorate(ReelStore, {
  errorMessage: observable,
  isDialogOpen: observable,
  reelClips: observable,
  isCreatingClip: observable,
  fetchingClips: observable,
  clipTitle: observable,
  addWatermark: observable,
  applyCrossfade: observable,
  cropRatio: observable,
  watermark: observable,
  titleError: observable,
  concatPlays: action,
  removeClip: action,
  addClipMetadata: action,
  reelDuration: computed
});

export default ReelStore;



// WEBPACK FOOTER //
// ./src/stores/ReelStore.js